import { G } from 'smg/querystring';

interface getRet<T> {
    rows: T[]; nextPage: boolean;
}
type drawRet = HTMLElement

export interface PageLoaderOptions<T> {
    get: () => getRet<T> | Promise<getRet<T>>;
    draw: (row: T) => drawRet | Promise<drawRet>;
}

export class PageLoader<T> {
    private parent = document.querySelector('#rows-parent') as HTMLElement;
    private spinner = document.querySelector('#rows-spinner-parent') as HTMLElement;

    private sortEl = document.querySelector('#sort') as HTMLSelectElement | null;

    private currPage = parseInt(G().Get('p') || '0', 10) || 0;
    private globalLoadingSym!: symbol;

    private rowNumContainers = document.querySelectorAll('.row-num-container');
    private firstRowNums = document.querySelectorAll('.first-row-num');
    private lastRowNums = document.querySelectorAll('.last-row-num');

    private prevPageBtns = document.querySelectorAll('.prev-page') as NodeListOf<HTMLButtonElement>;
    private nextPageBtns = document.querySelectorAll('.next-page') as NodeListOf<HTMLButtonElement>;

    private searchForm = document.querySelector('#search-form') as HTMLFormElement;
    private searchEl = document.querySelector('#search') as HTMLInputElement;

    private get: PageLoaderOptions<T>['get'];
    private draw: PageLoaderOptions<T>['draw'];

    constructor(opts: PageLoaderOptions<T>) {
        this.get = opts.get;
        this.draw = opts.draw;

        this.sortEl?.addEventListener('change', () => {
            if (this.sortEl?.selectedIndex) {
                G().Set('sort', this.sortEl.value);
            } else {
                G().Delete('sort');
            }

            this.setPage(0);

            void this.loadPage();
        });

        this.prevPageBtns.forEach(el => el.addEventListener('click', () => {
            this.prevPage();
            void this.loadPage();
        }));
        this.nextPageBtns.forEach(el => el.addEventListener('click', () => {
            this.nextPage();
            void this.loadPage();
        }));

        this.setPage(this.currPage);

        this.searchForm.addEventListener('submit', e => {
            e.preventDefault();

            const q = this.searchEl.value.trim();
            if (q) {
                G().Set('q', q);
            } else {
                G().Delete('q');
            }

            this.setPage(0);

            void this.loadPage();
        });

        const q = G().Get('q')?.trim() || '';
        if (q) {
            this.searchEl.value = q;
        }

        const sort = G().Get('sort')?.trim() || '';
        if (sort && this.sortEl) {
            this.sortEl.value = sort;
            if (this.sortEl.selectedIndex === -1) {
                this.sortEl.selectedIndex = 0;
                G().Delete('sort');
            }
        }
    }

    private setPage(p: number) {
        this.currPage = p;

        if (this.currPage < 0) {
            this.currPage = 0;
        }

        if (this.currPage) {
            G().Set('p', String(this.currPage));
        } else {
            G().Delete('p');
        }
    }

    private prevPage() {
        this.setPage(this.currPage - 1);
    }

    private nextPage() {
        this.setPage(this.currPage + 1);
    }

    private getPageSize() {
        return parseInt(G().Get('max') || '0', 10) || 100;
    }

    private async loadPage() {
        this.parent.classList.add('opacity-50');
        this.spinner.classList.remove('d-none');

        this.nextPageBtns.forEach(el => el.disabled = true);

        const localLoadingSym = this.globalLoadingSym = Symbol();
        const { rows, nextPage } = await this.get();
        if (localLoadingSym !== this.globalLoadingSym) {
            return;
        }

        const f = document.createDocumentFragment();

        for (const row of rows) {
            f.appendChild(await this.draw(row));
        }

        this.parent.innerHTML = '';
        this.parent.appendChild(f);

        this.parent.classList.remove('opacity-50');
        this.spinner.classList.add('d-none');

        if (rows.length) {
            this.firstRowNums.forEach(el => el.textContent = (this.getPageSize() * this.currPage + 1).toLocaleString());
            this.lastRowNums.forEach(el => el.textContent = (this.getPageSize() * this.currPage + rows.length).toLocaleString());
            this.rowNumContainers.forEach(el => el.classList.remove('d-none'));
        } else {
            this.rowNumContainers.forEach(el => el.classList.add('d-none'));
        }

        this.prevPageBtns.forEach(el => {
            el.disabled = !this.currPage;
        });
        this.nextPageBtns.forEach(el => {
            el.disabled = !nextPage;
        });
    }

    Load() {
        void this.loadPage();
    }

    Sort() {
        return this.sortEl?.value;
    }

    CurrentPageSize() {
        return this.getPageSize();
    }

    CurrentPage() {
        return this.currPage;
    }

    Search() {
        return this.searchEl.value;
    }
}